import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { HeroBlock } from "./blocks/HeroBlock";
import { BlockRenderer } from "./BlockRenderer";
import { Layout } from "@/components/layout/Layout";

interface CaseArticlePageProps {
  slug: string;
}

/**
 * Merge hotspot blocks into gallery images so hotspots render
 * directly on the gallery carousel instead of as separate blocks.
 */
function mergeHotspotsIntoGallery(blocks: any[]): any[] {
  // Collect all image_hotspot blocks into a map keyed by imageUrl
  const hotspotMap = new Map<string, { x: number; y: number; materialId: string }[]>();
  for (const block of blocks) {
    if (block.block_type === "image_hotspot") {
      const c = block.content as any;
      if (c?.imageUrl && c?.hotspots?.length) {
        const existing = hotspotMap.get(c.imageUrl) || [];
        hotspotMap.set(c.imageUrl, [...existing, ...c.hotspots]);
      }
    }
  }

  if (hotspotMap.size === 0) return blocks;

  return blocks
    .map((block) => {
      if (block.block_type === "gallery" && block.content?.images) {
        const images = (block.content.images as any[]).map((img: any) => {
          const matchedHotspots = hotspotMap.get(img.url);
          if (matchedHotspots) {
            return { ...img, hotspots: [...(img.hotspots || []), ...matchedHotspots] };
          }
          return img;
        });
        return { ...block, content: { ...block.content, images } };
      }
      return block;
    })
    // Hide standalone image_hotspot blocks whose image was merged into gallery
    .filter((block) => {
      if (block.block_type === "image_hotspot") {
        const c = block.content as any;
        // Keep it if its image didn't match any gallery image
        if (c?.imageUrl && hotspotMap.has(c.imageUrl)) {
          // Check if any gallery block actually contained this image
          const galleryBlocks = blocks.filter((b) => b.block_type === "gallery");
          for (const gb of galleryBlocks) {
            const imgs = (gb.content?.images || []) as any[];
            if (imgs.some((img: any) => img.url === c.imageUrl)) {
              return false; // merged, hide this block
            }
          }
        }
      }
      return true;
    });
}

export function CaseArticlePage({ slug }: CaseArticlePageProps) {
  const { data: article, isLoading: articleLoading } = useQuery({
    queryKey: ["case-article", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("case_articles")
        .select("*")
        .eq("slug", slug)
        .eq("published", true)
        .single();
      if (error) throw error;
      return data;
    },
  });

  const { data: rawBlocks } = useQuery({
    queryKey: ["case-article-blocks", article?.id],
    enabled: !!article?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("case_article_blocks")
        .select("*")
        .eq("article_id", article!.id)
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return data;
    },
  });

  const blocks = useMemo(
    () => (rawBlocks ? mergeHotspotsIntoGallery(rawBlocks) : []),
    [rawBlocks]
  );

  if (articleLoading) {
    return (
      <Layout>
        <div className="min-h-screen bg-muted flex items-center justify-center">
          <p className="font-sans text-muted-foreground">Loading…</p>
        </div>
      </Layout>
    );
  }

  if (!article) {
    return (
      <Layout>
        <div className="min-h-screen bg-muted flex items-center justify-center">
          <p className="font-sans text-muted-foreground">Article not found.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-muted pt-16 md:pt-20">
        <HeroBlock article={article} />
        {blocks.map((block: any) => (
          <BlockRenderer key={block.id} block={block} />
        ))}
      </div>
    </Layout>
  );
}
